import {Trans} from '@lingui/macro';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {UnreachableError} from '../../../libs/shared/helpers/src/types/customErrors';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {StyledBlockWrapper} from '../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../srcCommon/browser/openInBrowser';
import {LearnMoreButton} from '../../components/common/callToAction/learnMoreButton';
import {CustomFieldResourceTypesEnum} from '../../models/api/customFieldModel';
import {downloadFileAsync} from '../../services/browser';
import {SettingsSidebarInfoBox} from '../common/settingsSidebarInfoBox';
import {AllowedCustomFieldResourceImportType} from './allowedCustomFieldsResourceImportTypeHelper';
import {
  DownloadTemplateLink,
  DownloadTemplateLinkTypesEnum,
} from './settingsCustomFieldAttributeImportCsvTypes';

/*
 * Props.
 */

interface ImportSidebarInfoBoxProps {
  downloadTemplateCsvLink: DownloadTemplateLink;
  resourceType: AllowedCustomFieldResourceImportType;
}

/*
 * Style.
 */

const StyledButton = styled(Button)`
  ${StyledBlockWrapper} {
    flex-grow: 1;
  }
  margin-top: 8px;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledButtonIcon = styled(Icon)`
  margin-right: 8px;
`;

const StyledLearnMoreButton = styled(LearnMoreButton)`
  margin-top: 8px;
`;

/*
 * Component.
 */

export const SettingsCustomFieldAttributeImportCsvSidebarInfoBox: FC<ImportSidebarInfoBoxProps> = (props) => {
  const theme = useUserTheme();
  const {downloadTemplateCsvLink, resourceType} = props;

  const helpPageUrl = useMemo(() => {
    switch (resourceType) {
      case CustomFieldResourceTypesEnum.TEAMMATE:
        return 'https://help.front.com/t/60hwfhk/custom-teammate-fields';
      case CustomFieldResourceTypesEnum.INBOX:
        return 'https://help.front.com/t/m1hwfh1/custom-inbox-fields';
      default:
        throw new UnreachableError(resourceType);
    }
  }, [resourceType]);

  const onDownloadTemplateClick = () => {
    switch (downloadTemplateCsvLink.type) {
      case DownloadTemplateLinkTypesEnum.LINK:
        openInBrowser(downloadTemplateCsvLink.url);
        return;

      case DownloadTemplateLinkTypesEnum.FILE:
        downloadFileAsync(downloadTemplateCsvLink.url, downloadTemplateCsvLink.name);
        return;

      default:
        throw new UnreachableError(downloadTemplateCsvLink);
    }
  };

  return (
    <div>
      <SettingsSidebarInfoBox
        title={
          <Trans id="custom-field-attribute-import-info-template-title">Updating custom field data</Trans>
        }
        body={
          <div>
            <Trans id="custom-field-attribute-import-info-template-body-intro">
              If you have many custom field updates to make, you may want to edit several {resourceType}{' '}
              records at the same time.
              <ul>
                <li>To start, export your current custom field data.</li>
                <li>
                  After making edits with a file editor, upload your modified .csv file to update the custom
                  field data.
                </li>
              </ul>
            </Trans>
            <StyledLearnMoreButton variant={ButtonVariantsEnum.LINK} helpPageUrl={helpPageUrl} />
          </div>
        }
      />
      <StyledButton variant={ButtonVariantsEnum.SECONDARY} onClick={onDownloadTemplateClick}>
        <StyledButtonContentDiv>
          <StyledButtonIcon name="messageTemplateSmall" color={theme.palette.blue.shade40} />
          <Trans id="custom-field-attribute-import-info-template-body-download-template">
            Export custom field data
          </Trans>
        </StyledButtonContentDiv>
      </StyledButton>
    </div>
  );
};
